import { TreeNode } from 'primeng/api'

import { DataMapping, Slice } from '@flowx/designer/modules/core/dictionary/data-mapping.dictionary'
import {
  DataModelAttribute,
  DataModelAttributeType,
  DataModelDisplayType,
  DataModelEntityType,
  DataModelUiEntity,
  TreeNodeData,
} from '@flowx/designer/modules/core/dictionary/data-model.dictionary'
import { getStyleClassByType } from '@flowx/designer/modules/data-model-builder/services/data-model-builder.utils'
import {
  acceptsAttributes,
  nodesAlongPath,
} from '@flowx/designer/shared/components/data-mappers-form/data-mapper-suggestions'
import {
  CreateDataModelAttributeFromPath,
  CreateDataModelAttributeParams,
  dataModelEntityDisplayName,
  getAttributeTypeDisplay,
} from '@flowx/designer/shared/entities/data-model.entity'

export type PendingAttribute = {
  stringPath: string
  parentRefPath: string
  refPath: string
  attribute: CreateDataModelAttributeParams
}

const STAGED_KEY_MARKER = 'A:PENDING:'

const LOCAL_ENTITY_TYPE = DataModelEntityType.PROCESS

const LOCAL_LEGEND_CLASS = getStyleClassByType(LOCAL_ENTITY_TYPE)

const EMPTY_OBJECT_CLASS = 'disabled'

let stagedCounter = 0

export function keepStagedKeys(pending: PendingAttribute[]): void {
  const highest = pending.reduce((max, { refPath }) => {
    const suffix = Number(refPath?.split(STAGED_KEY_MARKER)[1])

    return Number.isFinite(suffix) && suffix > max ? suffix : max
  }, 0)

  stagedCounter = Math.max(stagedCounter, highest)
}

export function stageAttribute(
  stringPath: string,
  attribute: CreateDataModelAttributeParams,
  tree: TreeNode<TreeNodeData>[]
): PendingAttribute | null {
  const parentSegments = stringPath.split('.').slice(0, -1)
  const parents = nodesAlongPath(parentSegments, tree)

  if (parents.length !== parentSegments.length) {
    return null
  }

  const parent = parents.at(-1)

  return !parent || acceptsAttributes(parent)
    ? stagedNode(stringPath, attribute, parent?.key ?? '')
    : null
}

export function stagePath(
  stringPath: string,
  attribute: CreateDataModelAttributeParams,
  tree: TreeNode<TreeNodeData>[]
): PendingAttribute[] {
  const parentSegments = stringPath.split('.').slice(0, -1)
  const existing = nodesAlongPath(parentSegments, tree)

  if (existing.length === parentSegments.length) {
    const staged = stageAttribute(stringPath, attribute, tree)

    return staged ? [staged] : []
  }

  const anchor = existing.at(-1)

  if (anchor && !acceptsAttributes(anchor)) {
    return []
  }

  const missing = parentSegments.slice(existing.length)

  const intermediates = missing.reduce<PendingAttribute[]>((chain, segment, index) => {
    const path = parentSegments.slice(0, existing.length + index + 1).join('.')
    const parentRefPath = chain.at(-1)?.refPath ?? anchor?.key ?? ''

    return [...chain, stagedNode(path, objectAttribute(segment), parentRefPath)]
  }, [])

  return [...intermediates, stagedNode(stringPath, attribute, intermediates.at(-1).refPath)]
}

export function splitAtExisting(
  stringPath: string,
  tree: TreeNode<TreeNodeData>[]
): { existing: string[]; created: string[] } {
  const segments = stringPath.split('.')
  const depth = nodesAlongPath(segments.slice(0, -1), tree).length

  return { existing: segments.slice(0, depth), created: segments.slice(depth) }
}

function stagedNode(
  stringPath: string,
  attribute: CreateDataModelAttributeParams,
  parentRefPath: string
): PendingAttribute {
  const key = `${STAGED_KEY_MARKER}${(stagedCounter += 1)}`

  return {
    stringPath,
    parentRefPath,
    refPath: parentRefPath ? `${parentRefPath}.${key}` : key,
    attribute: { ...attribute, name: stringPath.split('.').at(-1) },
  }
}

function objectAttribute(name: string): CreateDataModelAttributeParams {
  return {
    name,
    type: DataModelAttributeType.OBJECT,
    multiInstance: false,
  } as CreateDataModelAttributeParams
}

function pendingTypeDisplay(attribute: CreateDataModelAttributeParams): DataModelDisplayType {
  if (attribute.type !== DataModelAttributeType.OBJECT) {
    return getAttributeTypeDisplay(attribute as DataModelAttribute)
  }

  const typeName = dataModelEntityDisplayName({
    name: attribute.name,
    type: LOCAL_ENTITY_TYPE,
  } as DataModelUiEntity)

  return { typeContent: `${typeName} { }`, displayClassName: 'font-semibold', id: '', valid: true }
}

function isMappable({ attribute }: PendingAttribute): boolean {
  return attribute.type !== DataModelAttributeType.OBJECT
}

export function treeWithPending(
  tree: TreeNode<TreeNodeData>[],
  pending: PendingAttribute[]
): TreeNode<TreeNodeData>[] {
  if (!pending.length) {
    return tree
  }

  const childrenByParent = pending.reduce<Map<string, PendingAttribute[]>>(
    (aggregate, attribute) =>
      aggregate.set(attribute.parentRefPath, [
        ...(aggregate.get(attribute.parentRefPath) ?? []),
        attribute,
      ]),
    new Map()
  )

  const attach = (
    nodes: TreeNode<TreeNodeData>[],
    parentRefPath: string
  ): TreeNode<TreeNodeData>[] => [
    ...nodes.map((node) => {
      const children = attach(node.children ?? [], node.key)

      if (!children.length) {
        return node
      }

      return node.children?.length ? { ...node, children } : { ...hasChildren(node), children }
    }),
    ...(childrenByParent.get(parentRefPath) ?? []).map((attribute) =>
      pendingNode(attribute, childrenByParent)
    ),
  ]

  return attach(tree, '')
}

function hasChildren(node: TreeNode<TreeNodeData>): TreeNode<TreeNodeData> {
  return {
    ...node,
    data: { ...node.data, state: { ...node.data.state, disabled: false } },
    styleClass: node.styleClass
      ?.split(' ')
      .filter((className) => className !== EMPTY_OBJECT_CLASS)
      .join(' '),
  }
}

export function pendingSliceItems(pending: PendingAttribute[]): Slice[] {
  return pending.filter(isMappable).map(({ refPath, stringPath, attribute }) => ({
    refPath,
    stringPath,
    optional: false,
    type: attribute.type,
    multiInstance: false,
  }))
}

export function unusedPendingTypes(pending: PendingAttribute[], activePaths: string[]): string[] {
  return pending
    .filter((item) => !isMappable(item))
    .map(({ stringPath }) => stringPath)
    .filter((path) => !activePaths.some((active) => active.startsWith(`${path}.`)))
}

export function isStagedRefPath(refPath: string): boolean {
  return !!refPath?.includes(STAGED_KEY_MARKER)
}

export type MapperSaveBody = {
  sliceItems: Slice[]
  mapping: DataMapping[]
  newSourceAttributes: CreateDataModelAttributeFromPath[]
}

export function mapperSaveBody(staged: {
  slice: Slice[]
  mappings: DataMapping[]
  pendingAttributes?: PendingAttribute[]
}): MapperSaveBody {
  return {
    sliceItems: staged.slice.map(withoutStagedSliceRefPath),
    mapping: staged.mappings.map(withoutStagedMappingRefPaths),
    newSourceAttributes: newSourceAttributes(staged.pendingAttributes ?? []),
  }
}

function newSourceAttributes(pending: PendingAttribute[]): CreateDataModelAttributeFromPath[] {
  const byPath = new Map(pending.filter(isMappable).map((item) => [item.stringPath, item]))

  return [...byPath.values()].map(({ stringPath, attribute }) => {
    const { name, ...fields } = attribute

    return { ...fields, path: stringPath, validators: attribute.validators ?? [] }
  })
}

function withoutStagedSliceRefPath<T extends Slice>({ refPath, ...item }: T): T {
  return (isStagedRefPath(refPath) ? item : { ...item, refPath }) as T
}

function withoutStagedMappingRefPaths({
  sourceRefPath,
  targetRefPath,
  ...mapping
}: DataMapping): DataMapping {
  return {
    ...mapping,
    ...(isStagedRefPath(sourceRefPath) ? {} : { sourceRefPath }),
    ...(isStagedRefPath(targetRefPath) ? {} : { targetRefPath }),
  } as DataMapping
}

export function nodeForPath(
  path: string,
  tree: TreeNode<TreeNodeData>[]
): TreeNode<TreeNodeData> | null {
  const segments = path.split('.')
  const covered = nodesAlongPath(segments, tree)

  return covered.length === segments.length ? (covered.at(-1) ?? null) : null
}

export function sliceItemForPath(path: string, tree: TreeNode<TreeNodeData>[]): Slice | null {
  const node = nodeForPath(path, tree)

  return node
    ? {
        refPath: node.key,
        stringPath: node.data.entity.stringPath,
        optional: false,
        type: node.data.entity.type,
        multiInstance: node.data.entity.multiInstance,
      }
    : null
}

function pendingNode(
  { refPath, stringPath, attribute }: PendingAttribute,
  childrenByParent: Map<string, PendingAttribute[]>
): TreeNode<TreeNodeData> {
  const children = (childrenByParent.get(refPath) ?? []).map((child) =>
    pendingNode(child, childrenByParent)
  )

  return {
    label: attribute.name,
    key: refPath,
    ...(children.length && { children }),
    data: {
      entity: {
        stringPath,
        optional: false,
        type: attribute.type,
        multiInstance: false,
      },
      state: {
        displayTypeObject: pendingTypeDisplay(attribute),
        exampleValue: attribute.exampleValue,
      },
    },
    styleClass: LOCAL_LEGEND_CLASS,
  }
}
